import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Typography,
  TextField,
  Autocomplete,
} from "@mui/material";
import { Pencil, Trash2 } from "lucide-react";
import ReactiveDialog from "./ReactiveDialog";
import { FormErrors } from "../family/types";
import {
  EntityMetadata,
  EntityMetadata_EntityMetadataField,
} from "../../protogen/common_pb";
import { PlainMessage } from "@bufbuild/protobuf";

// For families
export const ADVISOR_ATTRIBUTION_FIELDS = ["Channel"];
export const FAMILY_ATTRIBUTION_FIELDS = ["Referrer", "Channel"];
export const FAMILY_DEMOGRAPHICS_FIELDS = [
  "Is Parent",
  "Number of Children",
  "Home Value",
  "Relationship Status",
];

export const FAMILY_CHURN_REASONS = [
  "Underutilization",
  "Advisor Churn",
  "Budget",
  "Service Mismatch",
];

export const FAMILY_CHURN_REASONS_ADMIN_ONLY = [
  "Dissatisfied with Advisor",
  "Test/Influencer",
];

const AUTOCOMPLETE_MAPPING: {
  [key: string]: {
    [key: string]: string[];
  };
} = {
  family: {
    channel: [
      "Client Referral",
      "Connector/Influencer Program (Gifted Service)",
      "Direct Mail",
      "Direct Sales",
      "FA Direct",
      "FA Referral",
      "Influencer",
      "LinkedIn",
      "Local Marketing",
      "Loop + Tie",
      "Organic Social",
      "Other/Unattributed",
      "Paid Media",
      "Partnerships",
      "Press/Podcast",
      "Realtor Partnerships",
      "Word of Mouth",
    ],
    referrer: ["N/A"],
    owner: ["Emily", "Jackie", "Liz"],
    outlier: ["Test", "Project client", "Other"],
    "Relationship status": [
      "Single",
      "Married",
      "Divorced",
      "Widowed",
      // "Partner",
      "Couple",
      "Single, Dad",
      "Single, Mom",
      "Single, Parent",
      "Unknown",
      // "Separated", // Do we want this? pretty similar to divorced
      "Domestic Partner", // Do we want this? pretty similar to partner/couple/married
    ],
    "Number of children": [
      "0",
      "1",
      "2",
      "3",
      "4",
      "5",
      "6",
      "7",
      "8",
      "9",
      "10",
    ],
    "Is parent": ["Yes", "No"],
    "Home value": ["Unknown"],
    "Churn reason": [
      ...FAMILY_CHURN_REASONS,
      ...FAMILY_CHURN_REASONS_ADMIN_ONLY,
    ],
  },
  advisor: {
    channel: [
      "Client Referral",
      "Direct Sales",
      "FA Referral",
      "Influencer",
      "LinkedIn",
      "Local Marketing",
      "Networking",
      "Organic Social",
      "Other/Unattributed",
      "Paid Media",
      "Podcast",
      "Press/Podcast",
      "Word of Mouth",
      "Word of Mouth/Organic",
    ],
  },
};

export const fromProto = (meta: EntityMetadata) => {
  return Object.fromEntries(
    meta.fields.map((field: EntityMetadata_EntityMetadataField) => [
      field.key,
      field.value,
    ]),
  );
};

export const toProto = (obj: { [key: string]: string }) => {
  return {
    fields: Object.entries(obj).map(([key, value]) => ({
      key,
      value,
    })),
  };
};

type MetadataRow = {
  ind: number;
  key: string;
  value: string;
  deleted: boolean;
};

export const EditMetadataModal = ({
  metadata,
  handleSave,
  open,
  handleClose,
  entityType,
  missingKeys = [],
}: {
  metadata: { [key: string]: string };
  handleSave: (metadata: { [key: string]: string }) => Promise<void>;
  open: boolean;
  handleClose: () => void;
  entityType?: "family" | "advisor";
  missingKeys?: string[];
}) => {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<FormErrors | null>(null);

  // Create initial metadata with existing data and pre-populated missing keys
  const getInitialMetadata = () => {
    const existingKeys = Object.keys(metadata);
    const initialRows: MetadataRow[] = [];
    let ind = 0;

    // Add existing metadata
    Object.entries(metadata).forEach(([key, value]) => {
      initialRows.push({ ind: ind++, key, value, deleted: false });
    });

    // Add missing keys with empty values
    missingKeys.forEach((key) => {
      if (!existingKeys.includes(key)) {
        initialRows.push({ ind: ind++, key, value: "", deleted: false });
      }
    });

    // If no rows, add one empty row
    if (initialRows.length === 0) {
      initialRows.push({ ind: 0, key: "", value: "", deleted: false });
    }

    return initialRows;
  };

  const [editableMetadata, setEditableMetadata] = useState<MetadataRow[]>([]);

  useEffect(() => {
    if (open) {
      setEditableMetadata(getInitialMetadata());
    }
  }, [open]);

  const handleAddField = () => {
    setEditableMetadata((v) => [
      ...v,
      {
        ind: v.length,
        key: "",
        value: "",
        deleted: false,
      },
    ]);
  };

  const handleDeleteField = (key: string) => {
    setEditableMetadata((v) =>
      v.map((m) => (m.key === key ? { ...m, deleted: true } : m)),
    );
  };

  const validateMetadata = () => {
    const errors: FormErrors = {};
    const seenKeys = new Set<string>();
    editableMetadata.forEach((row) => {
      if (row.deleted) return;
      if (!row.key || !row.value) {
        if (!missingKeys.includes(row.key)) {
          errors[row.key] = "Key and value are required";
        }
      }
      if (seenKeys.has(row.key)) {
        errors[row.key] = "Duplicate key";
      }
      seenKeys.add(row.key);
    });
    setErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const onSave = async () => {
    if (!validateMetadata()) return;
    setLoading(true);
    await handleSave(
      Object.fromEntries(
        editableMetadata
          .filter((m) => !m.deleted)
          .filter((m) => !(missingKeys.includes(m.key) && !m.value))
          .map((m) => [m.key, m.value]),
      ),
    );
    setLoading(false);
    handleClose();
  };

  return (
    <ReactiveDialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      title="Edit Metadata"
      primaryActionName="Save"
      primaryAction={onSave}
      primaryActionEnabled={!loading}
    >
      {editableMetadata
        .filter((e) => !e.deleted)
        .map((entry) => (
          <Box
            key={entry.ind}
            sx={{ display: "flex", alignItems: "center", gap: "8px", mb: 2 }}
          >
            {/* KEY Autocomplete */}
            <Autocomplete
              freeSolo
              options={
                entityType && entityType in AUTOCOMPLETE_MAPPING
                  ? Object.keys(AUTOCOMPLETE_MAPPING[entityType]).filter(
                      (k) => !editableMetadata.map((m) => m.key).includes(k),
                    )
                  : []
              }
              fullWidth
              value={entry.key}
              onChange={(_, newValue) => {
                setEditableMetadata((v) =>
                  v.map((m) =>
                    m.ind === entry.ind ? { ...m, key: newValue || "" } : m,
                  ),
                );
              }}
              onInputChange={(_, newInputValue) => {
                // If user types a new value, record it
                setEditableMetadata((v) =>
                  v.map((m) =>
                    m.ind === entry.ind ? { ...m, key: newInputValue } : m,
                  ),
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Key"
                  error={!!errors?.[entry.key]}
                  fullWidth
                />
              )}
            />

            {/* VALUE Autocomplete */}
            <Autocomplete
              freeSolo
              fullWidth
              options={
                entityType &&
                entityType in AUTOCOMPLETE_MAPPING &&
                entry.key in AUTOCOMPLETE_MAPPING[entityType]
                  ? AUTOCOMPLETE_MAPPING[entityType][entry.key]
                  : []
              }
              value={entry.value}
              onChange={(_, newValue) => {
                setEditableMetadata((v) =>
                  v.map((m) =>
                    m.ind === entry.ind ? { ...m, value: newValue || "" } : m,
                  ),
                );
              }}
              onInputChange={(_, newInputValue) => {
                setEditableMetadata((v) =>
                  v.map((m) =>
                    m.ind === entry.ind ? { ...m, value: newInputValue } : m,
                  ),
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Value"
                  error={!!errors?.[entry.key]}
                  fullWidth
                />
              )}
            />

            <IconButton onClick={() => handleDeleteField(entry.key)}>
              <Trash2 height="20px" width="20px" stroke="#198282" />
            </IconButton>
          </Box>
        ))}
      <Button variant="text" onClick={handleAddField}>
        + Add another item
      </Button>
    </ReactiveDialog>
  );
};

type Props = {
  metadata: EntityMetadata;
  saveMetadata: (
    metadata: EntityMetadata | PlainMessage<EntityMetadata>,
  ) => Promise<void>;
  entityType?: "family" | "advisor";
};

export default ({ metadata, saveMetadata, entityType }: Props) => {
  const [localMetadata, setLocalMetadata] = useState<{
    [key: string]: string;
  }>(fromProto(metadata));

  const [open, setOpen] = useState(false);

  useEffect(() => {
    setLocalMetadata(fromProto(metadata));
  }, [metadata]);

  const handleSave = async (meta: { [key: string]: string }) => {
    await saveMetadata(toProto(meta));
    setLocalMetadata(meta);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: "12px",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="bodyHeavy">Metadata</Typography>
        <IconButton onClick={() => setOpen(true)}>
          <Pencil size={20} />
        </IconButton>
      </Box>
      {Object.keys(localMetadata).length === 0 ? (
        <Box sx={{ border: "1px dashed", padding: "16px" }}>
          <Typography>No metadata</Typography>
        </Box>
      ) : (
        <Box sx={{ border: "1px dashed", padding: "16px" }}>
          {Object.entries(localMetadata).map(([key, value]) => (
            <Typography key={key}>
              <strong>{key}:</strong> {value}
            </Typography>
          ))}
        </Box>
      )}
      <EditMetadataModal
        metadata={localMetadata}
        handleSave={handleSave}
        open={open}
        handleClose={() => setOpen(false)}
        entityType={entityType}
      />
    </Box>
  );
};
